'use strict';

angular.module('ringbaPortal.services').factory('callLogsService', callLogsService);

function callLogsService(_,
  $http,
  $log,
  $q,
  cacheService,
  dateTimeService,
  groupByService,
  moment,
  ngAuthSettings
) {
  'ngInject';

  const serviceBase = ngAuthSettings.apiServiceBaseUri;

  let pendingExportJobsChecked = false;
  const exportJobs = {
    jobs: {} // This associative array's reference is used in export-csv-loader directive
  };

  const callStatusMapping = {
    'CompletedCall': {
      class: 'call-status-yellow',
      legend: 'Completed - No Conversion',
      status: 'CompletedCall'
    },
    'CompletedConverted': {
      class: 'call-status-white',
      legend: 'Completed - With Conversion',
      status: 'CompletedConverted'
    },
    'BlockedCall': {
      class: 'call-status-red',
      legend: 'Blocked',
      status: 'BlockedCall'
    },
    'NoTargetsAnswered': {
      class: 'call-status-red',
      legend: 'Call Not Connected',
      status: 'NoTargetsAnswered'
    },
    'ConnectedCall': {
      class: 'call-status-green',
      legend: 'Live',
      status: 'ConnectedCall'
    }
  };

  const service = {
    getData: getData,
    getCallLogStatusMapping: getCallLogStatusMapping,
    getCallLogConversionAdjustmentMapping: getCallLogConversionAdjustmentMapping,
    exportData: exportData,
    getExportStatus: getExportStatus,
    checkLocalStoragePendingJobs: checkLocalStoragePendingJobs,
    exportJobs: exportJobs,
    addJobToExportJobsList: addJobToExportJobsList,
    removeJobFromExportJobsList: removeJobFromExportJobsList,
    removeJobsFromCache: removeJobsFromCache,
    transformDisplayName: transformDisplayName,
    exportCallLogsV2: exportCallLogsV2,
    getExportStatusV2: getExportStatusV2,
  };

  return service;

  function getData(accountId, options, include, viewAsBuyerId, viewAsPublisherId) {
    _.defaults(include, { graph: true, callLogs: true, groupBy: true });
    const url = _appendBuyerAndPublisherId(serviceBase + accountId + '/callLogs/Date', viewAsPublisherId, viewAsBuyerId);
    const params = {
      dateRange: _changeDatesToProperOptions(options.reportTime, options.timezoneId),
      timeSeries: _getTimeSeriesParam(include, options.graph),
      callLog: _getCallLogParam(include, options.callLogs),
      filters: _getFilters(include, options),
      sumGroupBys: _getSumGroupByParams(include, options.groupings),
      messageNames: groupByService.getMessageNamesFromColumns(options.eventFilters),
      tagFilters: options.tagFilters,
      timezoneId: options.timezoneId,
      tagSumGroupBys: _getTagGroupsParam(include, options.groupings)
    };

    return $http({
      url: url,
      method: 'POST',
      data: JSON.stringify(params),
      headers: { 'Content-Type': 'application/json' }
    });
  }

  function exportData(accountId, options, exportName, viewAsBuyerId, viewAsPublisherId) {
    const include = {
      callLogs: true,
      graph: false,
      roleups: false
    };
    const params = {
      dateRange: _changeDatesToProperOptions(options.reportTime, options.timezoneId),
      timeSeries: _getTimeSeriesParam(include, options.graph),
      callLog: _getCallLogParam(include, options.callLogs),
      filters: _getFilters(include, options),
      sumGroupBys: _getSumGroupByParams(include, options.groupings),
      messageNames: groupByService.getMessageNamesFromColumns(options.eventFilters),
      tagFilters: options.tagFilters,
      timezoneId: options.timezoneId,
      tagSumGroupBys: _getTagGroupsParam(include, options.groupings)
    };

    pendingExportJobsChecked = true;

    _deletePaginationParams(params);

    const url = _appendBuyerAndPublisherId(_getExportBaseUrl(accountId) + '/csv', viewAsPublisherId, viewAsBuyerId);

    return $http.post(url, params).then(function (response) {
      const jobId = response.data.id;
      _addJobIdToCache(accountId, {
        jobId: jobId,
        exportName: exportName
      });
      return response;
    });
  }

  function getExportStatus(accountId, jobId) {
    return $http.get(_getExportBaseUrl(accountId) + '/' + jobId).then(function (response) {
      return response.data;
    });
  }

  function exportCallLogsV2(accountId, settings, exportName, dontStoreInCache) {
    pendingExportJobsChecked = true;
    const url = _getBaseCallLogsUrlV2(accountId) + '/export/csv';

    return $http.post(url, settings).then(function (response) {
      const jobId = response.data.id;
      if (!dontStoreInCache) {
        _addJobIdToCache(accountId, {
          jobId: jobId,
          exportName: exportName,
          newCallLogs: true
        });
      }
      return response;
    });
  }

  function getExportStatusV2(accountId, jobId) {
    const url = _getBaseCallLogsUrlV2(accountId) + '/export/' + jobId;

    return $http.get(url).then(function (response) {
      return response.data;
    });
  }

  function checkLocalStoragePendingJobs(accountId) {
    if (!pendingExportJobsChecked) {
      return _checkPendingJobsStatus(accountId);
    }
    return $q.when(false);
  }

  function addJobToExportJobsList(job) {
    exportJobs.jobs[job.jobId] = job;
  }

  function removeJobFromExportJobsList(accountId, jobId) {
    delete exportJobs.jobs[jobId];
    _removeJobIdFromCache(accountId, jobId);
  }

  function removeJobsFromCache(accountId) {
    pendingExportJobsChecked = false;
    exportJobs.jobs = {};
    cacheService.remove(_getPendingJobsCacheKey(accountId));
  }

  function transformDisplayName(displayName, isBuyer) {
    const transformationMap = {
      'Inbound #': 'Caller ID',
      'Conversion Amount': isBuyer ? 'Payout' : 'Revenue',
      'Time to Connect': 'TT Connect',
      'Duplicate': 'Dupe',
      'Affiliate': 'Publisher',
      'Target Name': 'Target',
      'Call Length': 'Duration',
      'Recording': 'Rec.',
      'Time To Call': 'TT Call'
    };
    return Object.hasOwn(transformationMap, displayName.trim()) ? transformationMap[displayName] : displayName;
  }

  function _getBaseCallLogsUrlV2(accountId) {
    return ngAuthSettings.apiServiceBaseUri + accountId + '/calllogs';
  }

  function _checkPendingJobsStatus(accountId) {
    const pendingJobs = cacheService.get(_getPendingJobsCacheKey(accountId));

    if (pendingJobs && !_.isEmpty(pendingJobs)) {
      const checkJobStatus = [],
        pendingJobsArray = [];

      _.forEach(pendingJobs, function (job) {
        pendingJobsArray.push(job);
        if (job.newCallLogs) {
          checkJobStatus.push(getExportStatusV2(accountId, job.jobId));
        } else {
          checkJobStatus.push(getExportStatus(accountId, job.jobId));
        }
      });

      return $q.all(checkJobStatus).then(function (responses) {
        _handleExportJobStatusResponse(pendingJobsArray, responses);
        pendingExportJobsChecked = true;
        return true;
      });

    }
    pendingExportJobsChecked = true;
    return $q.when(false);
  }

  function _handleExportJobStatusResponse(jobs, statusResponses) {
    _.forEach(statusResponses, function (status, index) {
      addJobToExportJobsList({
        jobId: jobs[index].jobId,
        status: status,
        name: jobs[index].exportName,
        newCallLogs: jobs[index].newCallLogs
      });
    });
  }

  function _appendBuyerAndPublisherId(url, viewAsPublisherId, viewAsBuyerId) {
    // use a url package to manage url and querystring params
    const queryStringPrefix = url.indexOf('?') > -1 ? '&' : '?';
    let returnValue = url;
    if (viewAsPublisherId) {
      returnValue += queryStringPrefix + 'affid=' + viewAsPublisherId;
    }
    if (viewAsBuyerId) {
      returnValue += (viewAsPublisherId ? '&' : queryStringPrefix) + 'byid=' + viewAsBuyerId;
    }
    return returnValue;
  }

  function _getSumGroupByParams(include, options) {
    if (include.groupBy) {
      return _.flatMap(options.groupBy, function (n) {
        return n.field;
      });
    } else {
      return [];
    }
  }
  function _getTagGroupsParam(include, options) {
    if (include.groupBy) {
      return _.flatMap(options.tagGroupBy, function (n) {
        return { key: n.type.type, value: n.name.name };
      });
    } else {
      return [];
    }
  }
  function _getFilters(include, options) {
    return options.filters;
  }

  function _getCallLogParam(include, options) {
    if (include.callLogs) {
      _.defaults(options, { page: 0, pageSize: 10, sort: 'dtStamp', sortDirection: 'desc' });
      return options;
    } else {
      return {};
    }
  }

  function _getTimeSeriesParam(include, options) {
    if (include.graph) {
      _.defaults(options, { timeGroup: 'hour', groupBy: [] });
      return options;

    } else {
      return null;
    }
  }


  function _changeDatesToProperOptions(reportTime, windowsZoneId) {
    const tz = dateTimeService.getTimeZoneMapping(windowsZoneId) || moment.tz.guess();
    _.defaults(reportTime, { startDate: moment().tz(tz), endDate: moment().tz(tz) });
    return {
      past: _.round(moment.duration(moment().tz(tz).startOf('day').diff(reportTime.startDate)).asDays()),
      days: _.round(moment.duration(reportTime.endDate.diff(reportTime.startDate)).asDays() + 1)
    };
  }

  function getCallLogStatusMapping(events, isPublisher) {
    let callStatus = '';
    const map = {};

    _.each(events, function (event) {
      map[event.name] = true;
    });

    if (map.CompletedCall && map.ConvertedCall) {
      callStatus = 'CompletedConverted';
    } else if (map.CompletedCall) {
      callStatus = 'CompletedCall';
    } else if (map.BlockedCall) {
      callStatus = 'BlockedCall';
    } else if (map.ConnectedCall) {
      callStatus = 'ConnectedCall';
    } else if (map.NoTargetsAnswered){
      callStatus='NoTargetsAnswered';
    }
    const status = _.clone(callStatusMapping[callStatus]) || {};

    if (isPublisher && callStatus === 'CompletedCall' && status.legend) {
      status.legend = 'Completed';
      status.class = 'call-status-white';
    }

    return status;
  }

  function getCallLogConversionAdjustmentMapping(callStatus, events) {
    if (callStatus !== 'CompletedConverted') {
      return '';
    }
    const conversionRequestStatus = {};
    const conversionMapping = {
      'ConversionAdjustmentRequested': 'pending',
      'ConversionAdjustmentApproved': 'approved',
      'ConversionAdjustmentRejected': 'rejected'
    };
    const map = {},
      openingRequests = [],
      closingRequests = [];

    _.each(events, function (event) {
      if (map[event.name]) {
        map[event.name]++;
      } else {
        map[event.name] = 1;
      }
      if (event.name === 'ConversionAdjustmentRequested') {
        openingRequests.push(event);
      } else if (event.name === 'ConversionAdjustmentApproved' || event.name === 'ConversionAdjustmentRejected') {
        closingRequests.push(event);
      }

    });

    if(openingRequests.length && openingRequests.length > closingRequests.length) {
      conversionRequestStatus.status = 'pending';
    } else if (openingRequests.length && openingRequests.length === closingRequests.length) {
      let ordered;
      try {
        ordered = _.orderBy(closingRequests, function(req) {
          return _.find(req.columns, { name: 'dtStamp' }).original;
        });
      } catch {
        ordered = closingRequests;
      } finally {
        conversionRequestStatus.status = conversionMapping[ordered[ordered.length - 1].name];
        try {
          conversionRequestStatus.adjustmentAmount = _.find(ordered[ordered.length - 1].columns, { name: 'AdjustmentAmount' }).original;
        } catch {
          conversionRequestStatus.adjustmentAmount = '';
        }

      }

    }
    return conversionRequestStatus;
  }

  function _addJobIdToCache(accountId, job) {
    let jobs = cacheService.get(_getPendingJobsCacheKey(accountId));
    if (!jobs) {
      jobs = {};
    }

    jobs[job.jobId] = job;
    cacheService.set(_getPendingJobsCacheKey(accountId), jobs, {
      persistence: true
    });
  }

  function _removeJobIdFromCache(accountId, jobId) {
    const jobs = cacheService.get(_getPendingJobsCacheKey(accountId));
    try {
      delete jobs[jobId];
      cacheService.set(_getPendingJobsCacheKey(accountId), jobs, {
        persistence: true
      });
    } catch (e) {
      $log.error(e);
    }
  }

  function _deletePaginationParams(params) {
    delete params.callLog.page;
    delete params.callLog.pageSize;
  }

  function _getPendingJobsCacheKey(accountId) {
    return 'pendingExportJobs-' + accountId;
  }

  function _getExportBaseUrl(accountId) {
    return ngAuthSettings.apiServiceBaseUri + accountId + '/callLogs/Date/Export';
  }
}
